import { useRef } from 'react';

import { useVoiceStore } from '../state/voice-ui.store';

/** The widget's design width (`w-80`) — also the resize minimum. */
export const WIDGET_BASE_WIDTH = 320;
/** Agreed growth ceiling: the design size +50% on each axis. */
export const WIDGET_GROWTH_FACTOR = 1.5;
/** Matches the drag hook's viewport margin so the grip stops where drags do. */
const EDGE_MARGIN = 16;

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), Math.max(min, max));
}

export interface IResizeInput {
    /** Panel width at drag start (rendered). */
    readonly baseWidth: number;
    /** Panel height at drag start (rendered — may include an earlier resize). */
    readonly baseHeight: number;
    /** Content height with the resize min-height cleared — the true minimum,
     *  and the 1.5× reference. Measured per gesture so the limit is state-aware
     *  (dialer, in-call, and history panels all have different natural heights). */
    readonly naturalHeight: number;
    readonly dx: number;
    readonly dy: number;
    /** Space to the viewport edge (minus margin) — caps growth below the
     *  factor limits on small screens. The minimum still wins over both. */
    readonly maxViewportWidth: number;
    readonly maxViewportHeight: number;
}

/**
 * How far the widget has been grown, as a unitless multiplier in
 * [1, WIDGET_GROWTH_FACTOR]. Published to CSS as `--voice-scale` so every
 * panel can express its type, padding and control heights relative to the
 * current size — resizing used to change only the outer box, leaving the
 * contents at their design dimensions with gutters and dead space around them.
 *
 * Driven by width alone: height is a min-height that varies per panel, so it
 * is not a stable reference for typography.
 */
export function widgetScale(size: { readonly width: number } | null): number {
    if (!size) return 1;
    return clamp(size.width / WIDGET_BASE_WIDTH, 1, WIDGET_GROWTH_FACTOR);
}

/** Pure clamp math for the resize grip — see the test file for the spec. */
export function clampResizedSize(input: IResizeInput): { width: number; height: number } {
    const maxWidth = Math.min(
        Math.round(WIDGET_BASE_WIDTH * WIDGET_GROWTH_FACTOR),
        input.maxViewportWidth,
    );
    const maxHeight = Math.min(
        Math.round(input.naturalHeight * WIDGET_GROWTH_FACTOR),
        input.maxViewportHeight,
    );
    return {
        width: clamp(Math.round(input.baseWidth + input.dx), WIDGET_BASE_WIDTH, maxWidth),
        height: clamp(Math.round(input.baseHeight + input.dy), input.naturalHeight, maxHeight),
    };
}

interface IResizeState {
    startX: number;
    startY: number;
    baseWidth: number;
    baseHeight: number;
    naturalHeight: number;
    maxViewportWidth: number;
    maxViewportHeight: number;
}

export interface IWidgetResizeHandlers {
    readonly onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    readonly onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
    readonly onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
    readonly onDoubleClick: () => void;
}

/**
 * Pointer-capture resize for the widget's bottom-right grip — the sizing
 * sibling of `useWidgetDrag`. Width is limited to the design width → +50%;
 * height to the CURRENT panel's natural content height → +50%, measured at
 * drag start by momentarily clearing the inline min-height (the stored size
 * is applied as min-height, so content is never clipped regardless of what
 * gets persisted). Double-click restores the design size.
 */
export function useWidgetResize(): IWidgetResizeHandlers {
    const setSize = useVoiceStore((s) => s.setSize);
    const resizeRef = useRef<IResizeState | null>(null);

    const measurePanel = (
        grip: HTMLElement,
    ): { panel: HTMLElement; naturalHeight: number } | null => {
        const panel = grip.closest<HTMLElement>('[data-slot="voice-widget-panel"]');
        if (!panel) return null;
        // Natural height = content height without the resize floor. Clearing
        // the inline min-height for one synchronous read is invisible — the
        // browser never paints between these statements.
        const inlineMinHeight = panel.style.minHeight;
        panel.style.minHeight = '';
        const naturalHeight = panel.offsetHeight;
        panel.style.minHeight = inlineMinHeight;
        return { panel, naturalHeight };
    };

    const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
        const measured = measurePanel(event.currentTarget);
        if (!measured) return;
        const rect = measured.panel.getBoundingClientRect();
        resizeRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            baseWidth: rect.width,
            baseHeight: rect.height,
            naturalHeight: measured.naturalHeight,
            maxViewportWidth: window.innerWidth - rect.left - EDGE_MARGIN,
            maxViewportHeight: window.innerHeight - rect.top - EDGE_MARGIN,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
    };

    const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
        const state = resizeRef.current;
        if (!state) return;
        setSize(
            clampResizedSize({
                ...state,
                dx: event.clientX - state.startX,
                dy: event.clientY - state.startY,
            }),
        );
    };

    const onPointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!resizeRef.current) return;
        resizeRef.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
    };

    return { onPointerDown, onPointerMove, onPointerUp, onDoubleClick: () => setSize(null) };
}
